import { useState, useMemo } from "react";
import { useStock } from "../App";
import { db } from "../firebase";
import { collection, addDoc } from "firebase/firestore";

const Icons = {
  Plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
  Filter: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>,
  ChevronLeft: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>,
  ChevronRight: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>,
  Tag: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
};

const EXPENSE_CATEGORIES = ["Rent", "Utilities", "Salary", "Supplies", "Marketing", "Maintenance", "Other"];

// Helper for category badge colors
const getCategoryColor = (cat) => {
  switch (cat) {
    case "Rent": return { bg: "#fee2e2", text: "#b91c1c" };
    case "Salary": return { bg: "#d1fae5", text: "#047857" };
    case "Utilities": return { bg: "#e0f2fe", text: "#0369a1" };
    case "Supplies": return { bg: "#fef3c7", text: "#b45309" };
    case "Marketing": return { bg: "#f3e8ff", text: "#7e22ce" };
    case "Maintenance": return { bg: "#ffedd5", text: "#c2410c" };
    default: return { bg: "#f1f5f9", text: "#475569" }; // Other/Uncategorized
  }
};

export default function ExpensesPage() {
  const { sales, balances, deductBalance } = useStock(); 
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Pagination & Filter States
  const [selectedMonth, setSelectedMonth] = useState("All Time");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    title: "", amount: "", wallet: "", category: "Other", date: new Date().toISOString().split('T')[0]
  });

  // Data processing memoized for smart performance
  const { availableMonths, filteredExpenses, totalFilteredExpense, totalAllTimeExpense, totalPages, paginatedExpenses } = useMemo(() => {
    const rawExpenses = sales.filter(s => s.status === "Expense").sort((a, b) => new Date(b.date) - new Date(a.date));
    
    let allTimeTotal = 0;
    const monthSet = new Set();
    
    rawExpenses.forEach(e => {
      allTimeTotal += (e.amountNum || 0);
      const d = new Date(e.date);
      if (!isNaN(d)) {
        monthSet.add(d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
      }
    });

    const aMonths = Array.from(monthSet).sort((a,b) => new Date(b) - new Date(a)); // Sort newest month first

    const filtered = selectedMonth === "All Time" 
      ? rawExpenses 
      : rawExpenses.filter(e => {
          const d = new Date(e.date);
          return !isNaN(d) && d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) === selectedMonth;
        });

    const filteredTotal = filtered.reduce((sum, e) => sum + (e.amountNum || 0), 0);

    const tPages = Math.ceil(filtered.length / itemsPerPage) || 1;
    const validPage = Math.min(currentPage, tPages);
    const paginated = filtered.slice((validPage - 1) * itemsPerPage, validPage * itemsPerPage);

    return { 
      availableMonths: aMonths, 
      filteredExpenses: filtered, 
      totalFilteredExpense: filteredTotal, 
      totalAllTimeExpense: allTimeTotal,
      totalPages: tPages, 
      paginatedExpenses: paginated 
    };
  }, [sales, selectedMonth, currentPage]);

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setCurrentPage(1); // Reset pagination on filter change
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!formData.wallet) return alert("Select a wallet to deduct from.");
    const cost = parseFloat(formData.amount);
    
    if (balances[formData.wallet] < cost) {
      return alert(`Insufficient balance in ${formData.wallet}! Available: ¥${balances[formData.wallet]}`);
    }

    setLoading(true);
    try {
      await deductBalance(formData.wallet, cost);
      const now = new Date();

      const newExpense = {
        id: `#EXP-${Math.floor(1000 + Math.random() * 9000)}`,
        customer: "Expense",
        itemsSummary: formData.title,
        category: formData.category, // NEW: Saving Category to DB
        amount: `¥${cost.toLocaleString(undefined, {minimumFractionDigits: 2})}`,
        amountNum: cost,
        status: "Expense",
        date: formData.date,
        time: now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        wallet: formData.wallet,
        createdAt: now
      };

      await addDoc(collection(db, "sales"), newExpense);
      setShowModal(false);
      setFormData({ title: "", amount: "", wallet: "", category: "Other", date: new Date().toISOString().split('T')[0] });
    } catch (err) {
      alert("Error saving expense: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, paddingBottom: 20 }}>
      
      {/* --- TOP: Balances & Stats --- */}
      <div className="grid-4" style={{ gap: 16 }}>
        {Object.entries(balances).map(([method, amount]) => (
          <div key={method} style={{ background: "#FFFFFF", padding: "20px", borderRadius: 16, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)", display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>{method} Balance</span>
            <span style={{ fontSize: 24, fontWeight: 800, color: "#0f172a", fontFamily: "system-ui" }}>¥{amount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
          </div>
        ))}
        
        <div style={{ background: "#fef2f2", padding: "20px", borderRadius: 16, border: "1px solid #fecaca", display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={{ fontSize: 13, color: "#991b1b", fontWeight: 600 }}>{selectedMonth === "All Time" ? "Total Expenses (All Time)" : `Expenses (${selectedMonth})`}</span>
          <span style={{ fontSize: 24, fontWeight: 800, color: "#b91c1c", fontFamily: "system-ui" }}>¥{totalFilteredExpense.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
        </div>
      </div>

      {/* --- MIDDLE: Expense Table & Controls --- */}
      <div style={{ background: "#FFFFFF", borderRadius: 16, border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)" }}>
        
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 14 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", margin: 0 }}>Expense Records</h3>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: "4px 0 0" }}>{filteredExpenses.length} records found</p>
          </div>
          
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            {/* Smart Filter */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", padding: "8px 14px", borderRadius: 10, border: "1px solid #e2e8f0" }}>
              <span style={{ color: "#64748b" }}>{Icons.Filter}</span>
              <select value={selectedMonth} onChange={handleMonthChange} style={{ background: "transparent", border: "none", outline: "none", fontSize: 13, fontWeight: 600, color: "#0f172a", cursor: "pointer" }}>
                <option value="All Time">All Time</option>
                {availableMonths.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <button onClick={() => setShowModal(true)} style={{ background: "#f59e0b", color: "white", border: "none", padding: "10px 16px", borderRadius: 10, fontSize: 13, cursor: "pointer", fontWeight: 700, display: "flex", alignItems: "center", gap: 6, boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.3)" }}>
              {Icons.Plus} Add Expense
            </button>
          </div>
        </div>

        <div className="table-responsive" style={{ minHeight: "300px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
                {["Date", "Expense ID", "Category", "Details", "Wallet Used", "Amount"].map((h) => (
                  <th key={h} style={{ textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 600, padding: "0 8px 12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedExpenses.length > 0 ? paginatedExpenses.map((e, i) => {
                const cat = e.category || "Uncategorized";
                const catStyle = getCategoryColor(cat);
                
                return (
                  <tr key={i} style={{ borderBottom: "1px solid #f8fafc", transition: "background 0.2s" }} onMouseEnter={ev => ev.currentTarget.style.background = "#f8fafc"} onMouseLeave={ev => ev.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "16px 8px", fontSize: 13, color: "#64748b", fontWeight: 500 }}>{e.date}</td>
                    <td style={{ padding: "16px 8px", fontSize: 13, color: "#6366f1", fontWeight: 700 }}>{e.id}</td>
                    <td style={{ padding: "16px 8px" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: catStyle.bg, color: catStyle.text, padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
                        {Icons.Tag} {cat}
                      </span>
                    </td>
                    <td style={{ padding: "16px 8px", fontSize: 13, color: "#0f172a", fontWeight: 600 }}>{e.itemsSummary}</td>
                    <td style={{ padding: "16px 8px", fontSize: 13, color: "#64748b", fontWeight: 600 }}>{e.wallet}</td>
                    <td style={{ padding: "16px 8px", fontSize: 14, color: "#b91c1c", fontWeight: 800 }}>{e.amount}</td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8", fontSize: 13 }}>No expenses recorded for this period.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, paddingTop: 20, borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              Showing Page <strong style={{ color: "#0f172a" }}>{Math.min(currentPage, totalPages)}</strong> of <strong style={{ color: "#0f172a" }}>{totalPages}</strong>
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                disabled={currentPage === 1}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: "1px solid #e2e8f0", background: currentPage === 1 ? "#f8fafc" : "#FFFFFF", color: currentPage === 1 ? "#cbd5e1" : "#0f172a", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                {Icons.ChevronLeft}
              </button>
              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                disabled={currentPage >= totalPages}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, border: "1px solid #e2e8f0", background: currentPage >= totalPages ? "#f8fafc" : "#FFFFFF", color: currentPage >= totalPages ? "#cbd5e1" : "#0f172a", cursor: currentPage >= totalPages ? "not-allowed" : "pointer" }}
              >
                {Icons.ChevronRight}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* --- ADD EXPENSE MODAL --- */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 20 }}>
          <div style={{ background: "#FFFFFF", padding: 32, borderRadius: 20, width: "100%", maxWidth: 420, boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)" }}>
            <h3 style={{ margin: "0 0 20px", color: "#0f172a", fontSize: 20, fontWeight: 800 }}>Record New Expense</h3>
            
            <form onSubmit={handleAddExpense} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Expense Category</label>
                <select required value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none", background: "#f8fafc", fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                  {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              
              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Reason / Details</label>
                <input required placeholder="e.g. June Electricity Bill" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", fontSize: 14 }} onFocus={e => e.target.style.borderColor = "#6366f1"} onBlur={e => e.target.style.borderColor = "#cbd5e1"} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Amount (¥)</label>
                <input type="number" step="any" required placeholder="0.00" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", fontSize: 16, fontWeight: 700, fontFamily: "system-ui" }} onFocus={e => e.target.style.borderColor = "#6366f1"} onBlur={e => e.target.style.borderColor = "#cbd5e1"} />
              </div>

              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Deduct From Wallet</label>
                <select required value={formData.wallet} onChange={e => setFormData({...formData, wallet: e.target.value})} style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none", background: "#f8fafc", fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
                  <option value="" disabled>Select Payment Wallet...</option>
                  <option value="FFC Alipay">FFC Alipay (Bal: ¥{balances["FFC Alipay"]?.toLocaleString(undefined, {minimumFractionDigits: 2})})</option>
                  <option value="FFC Wechat">FFC Wechat (Bal: ¥{balances["FFC Wechat"]?.toLocaleString(undefined, {minimumFractionDigits: 2})})</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: 12, color: "#64748b", fontWeight: 600, display: "block", marginBottom: 6 }}>Date</label>
                <input type="date" required value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid #cbd5e1", outline: "none", boxSizing: "border-box", fontSize: 14 }} onFocus={e => e.target.style.borderColor = "#6366f1"} onBlur={e => e.target.style.borderColor = "#cbd5e1"} />
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ flex: 1, padding: "14px", borderRadius: 10, border: "1px solid #e2e8f0", background: "white", cursor: "pointer", fontWeight: 700, color: "#64748b" }} disabled={loading}>Cancel</button>
                <button type="submit" style={{ flex: 1, padding: "14px", borderRadius: 10, border: "none", background: "#f59e0b", color: "white", fontWeight: 700, cursor: "pointer", opacity: loading ? 0.7 : 1, boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.3)" }} disabled={loading}>{loading ? "Saving..." : "Save Expense"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}